import React from "react";
import { Link } from "react-router-dom";
import PropTypes from "prop-types";
import {
  MapPin,
  Bed,
  Bath,
  Maximize2,
  Heart,
  ArrowRight,
  ShieldCheck,
} from "lucide-react";
import { formatPriceInr } from "./formatPrice";
import "./PropertyCard.css";

const FALLBACK_PROPERTY_IMAGE =
  "https://images.unsplash.com/photo-1600596542815-ffad4c1539a9?q=80&w=1000&auto=format&fit=crop";

/**
 * Shared PropertyCard component across public discovery pages
 * Supports Grid & List layouts, wishlist toggling, and Indian currency formatting.
 */
export const PropertyCard = ({
  property,
  layout = "grid",
  isWishlisted = false,
  onToggleWishlist,
  assetUrl = "",
  className = "",
}) => {
  if (!property) return null;

  const id = property.id;
  const title = property.name || property.title || "Exclusive Residence";
  const price = property.price || property.budget;
  const purpose = property.purpose?.name || property.purpose || "Sale";
  const type = property.type?.name || property.type || "Property";
  const isVerified = property.verified !== false;

  const rawImage =
    property.images?.[0]?.image_url ||
    property.image_url ||
    (Array.isArray(property.images) && property.images.length > 0 && property.images[0]) ||
    FALLBACK_PROPERTY_IMAGE;

  const imageUrl =
    typeof rawImage === "string" && rawImage.startsWith("http")
      ? rawImage
      : assetUrl
      ? `${assetUrl}${rawImage}`
      : rawImage || FALLBACK_PROPERTY_IMAGE;

  const address =
    property.address ||
    [property.area?.name, property.city?.name || property.area?.city?.name]
      .filter(Boolean)
      .join(", ") ||
    "Prime Location";

  const beds = property.structure?.bed_rooms ?? property.bedrooms ?? 3;
  const baths = property.structure?.bath_rooms ?? property.bathrooms ?? 3;
  const area =
    property.structure?.built_up_area ||
    property.built_up_area ||
    property.area_sqft ||
    "2,200";

  return (
    <article
      className={`ahd-prop-card ${
        layout === "list" ? "ahd-prop-card--list" : ""
      } ${className}`}
    >
      <div className="ahd-prop-card__media">
        <img
          src={imageUrl}
          alt={title}
          className="ahd-prop-card__img"
          loading="lazy"
        />

        <div className="ahd-prop-card__badges">
          <span className="ahd-prop-card__badge ahd-prop-card__badge--purpose">
            For {purpose}
          </span>
          <span className="ahd-prop-card__badge ahd-prop-card__badge--type">
            {type}
          </span>
          {isVerified && (
            <span className="ahd-prop-card__badge ahd-prop-card__badge--verified">
              <ShieldCheck size={11} />
              Verified
            </span>
          )}
        </div>

        {onToggleWishlist && (
          <button
            type="button"
            className={`ahd-prop-card__wishlist ${
              isWishlisted ? "ahd-prop-card__wishlist--active" : ""
            }`}
            onClick={(e) => onToggleWishlist(e, id)}
            aria-label={isWishlisted ? "Remove from wishlist" : "Add to wishlist"}
          >
            <Heart size={16} fill={isWishlisted ? "currentColor" : "none"} />
          </button>
        )}
      </div>

      <div className="ahd-prop-card__body">
        <div className="ahd-prop-card__price-row">
          <span className="ahd-prop-card__price">{formatPriceInr(price)}</span>
        </div>

        <h3 className="ahd-prop-card__title">
          <Link to={`/property-details/${id}`}>{title}</Link>
        </h3>

        <p className="ahd-prop-card__address">
          <MapPin size={14} />
          <span>{address}</span>
        </p>

        <div className="ahd-prop-card__specs">
          <div className="ahd-prop-card__spec">
            <Bed size={15} />
            <span>{beds} Beds</span>
          </div>
          <div className="ahd-prop-card__spec">
            <Bath size={15} />
            <span>{baths} Baths</span>
          </div>
          <div className="ahd-prop-card__spec">
            <Maximize2 size={15} />
            <span>{area} sq.ft</span>
          </div>
        </div>

        <div className="ahd-prop-card__footer">
          <Link
            to={`/property-details/${id}`}
            className="ahd-prop-card__btn-view"
          >
            <span>View Details</span>
            <ArrowRight size={14} strokeWidth={2.5} />
          </Link>
        </div>
      </div>
    </article>
  );
};

PropertyCard.propTypes = {
  property: PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
    name: PropTypes.string,
    title: PropTypes.string,
    price: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    budget: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    address: PropTypes.string,
    purpose: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
    type: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
    verified: PropTypes.bool,
    images: PropTypes.array,
    structure: PropTypes.object,
  }).isRequired,
  layout: PropTypes.oneOf(["grid", "list"]),
  isWishlisted: PropTypes.bool,
  onToggleWishlist: PropTypes.func,
  assetUrl: PropTypes.string,
  className: PropTypes.string,
};

export default PropertyCard;
